import { ref, computed } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useToast } from '@/composables/common/useToast'

export function useAbonadoShow(props) {
    const toast = useToast()

    // ==================== STATE ====================
    const abonado = ref(null)
    const loading = ref(false)
    const url_documentacion = ref(null)

    // ==================== COMPUTED ====================
    const configuracion = computed(() => store.getters.getConfig)
    const manager = computed(() => store.getters.getFooter)
    const teatro = computed(() => store.getters.getTeatro)

    // ==================== METHODS ====================

    /**
      * Carga los datos principales del abonado
      */
    async function getAbonado() {
        loading.value = true
        const params = {
            abonado_id: props.abonado_id,
            filters: {
                lista_abonado_id: props.lista_id
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.show'),
                params
            )
            abonado.value = res.data.data

            // Construir URL de documentación
            url_documentacion.value = 
                config.API_BASE + 
                '/f/upload/documento?tid=' + 
                configuracion.value.hash_teatro +
                '&abonado_id=' +
                props.abonado_id
        } catch (error) {
            console.error('Error fetching abonado:', error)
            toast.error('Error al cargar los datos del abonado')
        } finally {
            loading.value = false
        }
    }

    /**
     * Pagar un plazo específico
     */
    async function pagarPlazo(plazo_id, metodo_pago) {
        const params = {
            abonado_id: props.abonado_id,
            plazo_id: plazo_id,
            filters: {
                lista_abonado_id: props.lista_id,
                tid: configuracion.value.hash_teatro,
                metodo_pago: metodo_pago
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.plazos.pagar'),
                params
            )
            toast.success(res.data.message || 'Plazo pagado correctamente')
            await getAbonado()
        } catch (error) {
            console.error('Error paying plazo:', error)
            toast.error(error.response?.data?.message || 'Error al pagar el plazo')
        }
    }

    /**
     * Pagar todos los plazos
     */
    async function pagarPlazos(metodo_pago) {
        const params = {
            abonado_id: props.abonado_id,
            filters: {
                lista_abonado_id: props.lista_id,
                tid: configuracion.value.hash_teatro,
                metodo_pago: metodo_pago
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.plazos.pagar-todos'),
                params
            )
            toast.success(res.data.message || 'Todos los plazos pagados correctamente')
            await getAbonado()
        } catch (error) {
            console.error('Error paying plazos:', error)
            toast.error(error.response?.data?.message || 'Error al pagar los plazos')
        }
    }

    /**
      * Descontar un plazo con descuento integro o porcentaje
      */
    async function descontarPlazo(descuento_integro, descuento_porcentaje, plazo_id) {
        const params = {
            abonado_id: props.abonado_id,
            plazo_id: plazo_id,
            filters: {
                lista_abonado_id: props.lista_id,
                tid: configuracion.value.hash_teatro,
                descuento_integro: descuento_integro,
                descuento_porcentaje: descuento_porcentaje
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.plazos.descontar'),
                params
            )
            toast.success(res.data.message || 'Descuento aplicado correctamente')
            await getAbonado()
        } catch (error) {
            console.error('Error applying descuento:', error)
            toast.error('Error al intentar aplicar un descuento')
        }
    }

    /**
     * Envía al abonado un correo con el enlace a su perfil para pagar el plazo
     * fallido definitivo (estado 4).
     *
     * El backend además marca el abono como inactivo mientras la deuda siga
     * pendiente, por eso refrescamos el abonado tras el envío.
     */
    async function generarEnlacePago(plazo_id) {
        const params = {
            abonado_id: props.abonado_id,
            plazo_id: plazo_id,
            filters: {
                lista_abonado_id: props.lista_id,
                tid: configuracion.value.hash_teatro
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.plazos.enviar-url-compra'),
                params
            )
            toast.success(res.data.message || 'Enlace de pago enviado correctamente')
            await getAbonado()
            return res.data
        } catch (error) {
            console.error('Error sending payment link:', error)
            toast.error(error.response?.data?.message || 'Error al enviar el enlace de pago')
        }
    }

    /**
     * Editar renovación automática
     */
    async function editarRenovacionAutomatica(value) {
        if (!abonado.value) return

        const datos = {
            ...abonado.value,
            abonado_id: abonado.value.id,
            renovacion_automatica: value ? 1 : 0,
            filters: {
                lista_abonado_id: props.lista_id
            }
        }

        try {
            await store.dispatch(
                config.getVuexAlias('back.abonados.editar'),
                datos
            )
            toast.success('Renovación automática actualizada correctamente')
            await getAbonado()
        } catch (error) {
            console.error('Error updating renovacion_automatica:', error)
            const msg = error.response?.data?.message || 'Error al actualizar la renovación automática'
            toast.error(msg)
        }
    }

    /**
     * Editar observación
     */
    async function editarObservacion(observacion) {
        if (!abonado.value) return

        const datos = {
            ...abonado.value, 
            abonado_id: abonado.value.id,
            observaciones: observacion,
            filters: {}
        }

        try {
            await store.dispatch(
                config.getVuexAlias('back.abonados.editar'),
                datos
            )
            toast.success('Observación editada correctamente')
            await getAbonado()
        } catch (error) {
            console.error('Error updating observacion:', error)
            toast.error('Ha ocurrido un error al editar la observación')
        }
    }

    /**
     * Editar datos legales
     */
    async function editarDatosLegales(datos) {
        if (!abonado.value) return

        const payload = {
            ...abonado.value,
            abonado_id: abonado.value.id,
            privacidad: abonado.value.privacidad,
            condiciones: abonado.value.condiciones,
            comunicaciones: datos.comunicaciones,
            condiciones_plazos: datos.condiciones_plazos,
            filters: {}
        }

        try {
            await store.dispatch(
                config.getVuexAlias('back.abonados.editar'),
                payload
            )
            toast.success('Datos legales editados correctamente')
            await getAbonado()
        } catch (error) {
            console.error('Error updating datos_legales:', error)
            toast.error('Ha ocurrido un error al editar los datos legales')
        }
    }

    /**
      * Enviar abono por email
      */
    async function enviarAbono() {
        const params = {
            abonado_id: props.abonado_id,
            filters: {
                lista_abonado_id: props.lista_id
            }
        }

        try {
            await store.dispatch(
                config.getVuexAlias('back.abonados.abono.enviar'),
                params
            )
            toast.success('Abono enviado correctamente')
        } catch (error) {
            console.error('Error sending abono:', error)
            toast.error('Error al enviar el abono')
        }
    }

    // ==================== INITIALIZATION ====================
    return {
        // State
        abonado,
        loading,
        url_documentacion,

        // Computed
        configuracion,
        manager,
        teatro,

        // Methods (show-only)
        getAbonado,
        pagarPlazo,
        pagarPlazos,
        descontarPlazo,
        generarEnlacePago,
        editarRenovacionAutomatica,
        editarObservacion,
        editarDatosLegales,
        enviarAbono,
    }
}
